import React, { useState, useRef, useEffect } from 'react';
import {
    Button,
    LinearProgress,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Alert,
    Chip,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    TextField,
    MenuItem,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import axiosInstance from '../authentication/AuthContext';
import { useUploadContext } from '../contexts/UploadContext';
import AppNavbar from '../components/navigation/AppNavbar';
import SideMenu from '../components/navigation/SideMenu';

import Header from '../components/header/Header';

export default function DocumentUploader() {
    const fileInputRef = useRef(null);
    const { uploads, enqueueUpload, cancelUpload, dismissUpload } =
        useUploadContext();

    // Files staged locally for review/metadata editing before being handed
    // off to the global upload queue (which lives in UploadContext so it
    // keeps running - and stays visible in the header - across navigation).
    const [files, setFiles] = useState([]);
    const [error, setError] = useState('');
    const [availableDepartments, setAvailableDepartments] = useState([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);

    useEffect(() => {
        const fetchDepartments = async () => {
            setDepartmentsLoading(true);
            try {
                const response = await axiosInstance.get(
                    '/accounts/departments/'
                );
                setAvailableDepartments(response.data);
            } catch (err) {
                console.error('Failed to fetch departments:', err);
            } finally {
                setDepartmentsLoading(false);
            }
        };

        fetchDepartments();
    }, []);

    const handleFileSelect = (event) => {
        const selectedFiles = Array.from(event.target.files);
        const newFiles = selectedFiles.map((file) => ({
            id: Date.now() + Math.random(),
            file,
            title: file.name,
            filename: file.name,
            size: file.size,
            uploaded_at: new Date().toISOString(),
            type: file.type,
            department: '',
        }));

        setFiles((prev) => [...prev, ...newFiles]);
        setError('');
    };

    const removeFile = (fileId) => {
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleUploadAll = () => {
        if (files.length === 0) return;

        files.forEach((fileItem) => {
            enqueueUpload({
                id: fileItem.id,
                file: fileItem.file,
                title: fileItem.title,
                filename: fileItem.filename,
                department: fileItem.department,
                uploaded_at: fileItem.uploaded_at,
            });
        });

        setFiles([]);
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClearAll = () => {
        setFiles([]);
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
                return <CheckCircleIcon color="success" />;
            case 'error':
                return <ErrorIcon color="error" />;
            case 'queued':
            case 'uploading':
            case 'processing':
                return <CircularProgress size={20} />;
            default:
                return <FileUploadIcon color="action" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'success':
                return 'success';
            case 'error':
                return 'error';
            case 'queued':
            case 'uploading':
            case 'processing':
                return 'primary';
            default:
                return 'default';
        }
    };

    // Human-readable label shown on an upload's status chip / progress row.
    const getDisplayLabel = (uploadItem) => {
        switch (uploadItem.status) {
            case 'queued':
                return 'Queued...';
            case 'uploading':
                return `${uploadItem.progress || 0}%`;
            case 'processing':
            case 'error':
            case 'canceled':
                return uploadItem.phaseLabel || uploadItem.status;
            case 'success':
                return 'Done';
            default:
                return uploadItem.status;
        }
    };

    const activeUploads = uploads.filter(
        (u) =>
            u.status === 'queued' ||
            u.status === 'uploading' ||
            u.status === 'processing'
    );
    const finishedUploads = uploads.filter(
        (u) =>
            u.status === 'success' ||
            u.status === 'error' ||
            u.status === 'canceled'
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <SideMenu />
            <AppNavbar />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    bgcolor: 'background.default',
                }}
            >
                <Header />
                <Box
                    sx={{
                        flex: 1,
                        width: '100%',
                        maxWidth: '100%',
                        px: { xs: 2, sm: 3 },
                        py: { xs: 2, sm: 3 },
                        pb: 5,
                        mt: { xs: 8, md: 0 },
                    }}
                >
                    <Grid container spacing={3} sx={{ width: '100%' }}>
                        {/* File Selection Area */}
                        <Grid item margin={3} xs={12} md={6}>
                            <Card width="100%">
                                <CardContent sx={{ minHeight: 400 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Select Files
                                    </Typography>

                                    <Box
                                        sx={{
                                            border: '2px dashed',
                                            borderColor: 'primary.main',
                                            borderRadius: 2,
                                            p: 3,
                                            width: '100%',
                                            textAlign: 'center',
                                            minHeight: 300,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                borderColor: 'primary.dark',
                                                backgroundColor: 'action.hover',
                                            },
                                        }}
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                    >
                                        <CloudUploadIcon
                                            sx={{
                                                fontSize: 48,
                                                color: 'primary.main',
                                                mb: 2,
                                            }}
                                        />
                                        <Typography
                                            variant="h6"
                                            color="primary"
                                        >
                                            Click to select files
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            or drag and drop files here
                                        </Typography>
                                    </Box>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        onChange={handleFileSelect}
                                        style={{ display: 'none' }}
                                        accept="*/*"
                                    />

                                    <Box
                                        sx={{ mt: 2, display: 'flex', gap: 1 }}
                                    >
                                        <Button
                                            variant="contained"
                                            onClick={handleUploadAll}
                                            disabled={files.length === 0}
                                            startIcon={<CloudUploadIcon />}
                                            fullWidth
                                            sx={{
                                                '&:disabled': {
                                                    backgroundColor: 'grey.200',
                                                    color: 'grey.600',
                                                    borderColor: 'grey.300',
                                                },
                                            }}
                                        >
                                            Upload All Files
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={handleClearAll}
                                            disabled={files.length === 0}
                                        >
                                            Clear All
                                        </Button>
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: 1 }}
                                    ></Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* File List */}
                        <Grid item margin={3} xs={12} md={6}>
                            <Card>
                                <CardContent sx={{ minHeight: 400 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Selected Files ({files.length})
                                    </Typography>

                                    {files.length === 0 ? (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            align="center"
                                        >
                                            No files selected
                                        </Typography>
                                    ) : (
                                        <List>
                                            {files.map((fileItem) => (
                                                <ListItem
                                                    key={fileItem.id}
                                                    divider
                                                    sx={{
                                                        alignItems:
                                                            'flex-start',
                                                        position: 'relative',
                                                        pb: 2,
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            fileItem.title ||
                                                            fileItem.filename
                                                        }
                                                        secondary={`${formatFileSize(fileItem.size)} • ${fileItem.type || 'Unknown type'}`}
                                                    />
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() =>
                                                            removeFile(
                                                                fileItem.id
                                                            )
                                                        }
                                                        sx={{
                                                            position:
                                                                'absolute',
                                                            top: 8,
                                                            right: 8,
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                    <Box
                                                        sx={{
                                                            mt: 2,
                                                            width: '100%',
                                                            display: 'grid',
                                                            gridTemplateColumns:
                                                                {
                                                                    xs: '1fr',
                                                                    sm: '1fr 1fr',
                                                                },
                                                            gap: 2,
                                                        }}
                                                    >
                                                        <TextField
                                                            label="Title"
                                                            size="small"
                                                            value={
                                                                fileItem.title
                                                            }
                                                            onChange={(e) =>
                                                                setFiles(
                                                                    (prev) =>
                                                                        prev.map(
                                                                            (
                                                                                f
                                                                            ) =>
                                                                                f.id ===
                                                                                fileItem.id
                                                                                    ? {
                                                                                          ...f,
                                                                                          title: e
                                                                                              .target
                                                                                              .value,
                                                                                      }
                                                                                    : f
                                                                        )
                                                                )
                                                            }
                                                        />
                                                        <TextField
                                                            select
                                                            label="Department"
                                                            size="small"
                                                            value={
                                                                fileItem
                                                                    .department
                                                                    ?.id ?? ''
                                                            }
                                                            onChange={(e) => {
                                                                const selectedId =
                                                                    e.target
                                                                        .value;
                                                                const selectedDepartment =
                                                                    selectedId ===
                                                                    ''
                                                                        ? null
                                                                        : (availableDepartments.find(
                                                                              (
                                                                                  dept
                                                                              ) =>
                                                                                  String(
                                                                                      dept.id
                                                                                  ) ===
                                                                                  String(
                                                                                      selectedId
                                                                                  )
                                                                          ) ??
                                                                          null);

                                                                setFiles(
                                                                    (prev) =>
                                                                        prev.map(
                                                                            (
                                                                                f
                                                                            ) =>
                                                                                f.id ===
                                                                                fileItem.id
                                                                                    ? {
                                                                                          ...f,
                                                                                          department:
                                                                                              selectedDepartment,
                                                                                      }
                                                                                    : f
                                                                        )
                                                                );
                                                            }}
                                                            disabled={
                                                                departmentsLoading
                                                            }
                                                            helperText={
                                                                departmentsLoading
                                                                    ? 'Loading departments...'
                                                                    : 'Select the owning department'
                                                            }
                                                        >
                                                            <MenuItem value="">
                                                                <em>None</em>
                                                            </MenuItem>
                                                            {availableDepartments.map(
                                                                (
                                                                    department
                                                                ) => {
                                                                    const name =
                                                                        department.name;
                                                                    const id =
                                                                        department.id;

                                                                    return (
                                                                        <MenuItem
                                                                            key={
                                                                                id
                                                                            }
                                                                            value={
                                                                                id
                                                                            }
                                                                        >
                                                                            {name ||
                                                                                'Unnamed department'}
                                                                        </MenuItem>
                                                                    );
                                                                }
                                                            )}
                                                        </TextField>
                                                    </Box>
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Upload Progress */}
                        {activeUploads.length > 0 && (
                            <Grid item margin={3} xs={12}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Upload Progress
                                        </Typography>
                                        {activeUploads.map((uploadItem) => (
                                            <Box
                                                key={uploadItem.id}
                                                sx={{ mb: 2 }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                        alignItems: 'center',
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        {uploadItem.title}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Typography
                                                            paddingLeft={1}
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {getDisplayLabel(
                                                                uploadItem
                                                            )}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                cancelUpload(
                                                                    uploadItem.id
                                                                )
                                                            }
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                                <LinearProgress
                                                    variant={
                                                        uploadItem.status ===
                                                        'processing'
                                                            ? 'indeterminate'
                                                            : 'determinate'
                                                    }
                                                    value={
                                                        uploadItem.progress || 0
                                                    }
                                                    color="primary"
                                                />
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Error Display */}
                        {error && (
                            <Grid item xs={12}>
                                <Alert
                                    severity="error"
                                    onClose={() => setError('')}
                                >
                                    {error}
                                </Alert>
                            </Grid>
                        )}

                        {/* Upload Results */}
                        {finishedUploads.length > 0 && (
                            <Grid item margin={3} xs={12}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Upload Results
                                        </Typography>
                                        <List dense>
                                            {finishedUploads.map((result) => (
                                                <ListItem
                                                    key={result.id}
                                                    divider
                                                    secondaryAction={
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            onClick={() =>
                                                                dismissUpload(
                                                                    result.id
                                                                )
                                                            }
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    }
                                                >
                                                    <Box
                                                        sx={{
                                                            mr: 1,
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        {getStatusIcon(
                                                            result.status
                                                        )}
                                                    </Box>
                                                    <ListItemText
                                                        primary={result.title}
                                                        secondary={
                                                            result.status ===
                                                            'success'
                                                                ? 'Upload successful'
                                                                : result.status ===
                                                                    'canceled'
                                                                  ? 'Canceled'
                                                                  : `Error: ${result.errorMessage}`
                                                        }
                                                    />
                                                    <Chip
                                                        label={getDisplayLabel(
                                                            result
                                                        )}
                                                        color={getStatusColor(
                                                            result.status
                                                        )}
                                                        size="small"
                                                        sx={{ mr: 4 }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
}
